// Path strings encode where a node lives in the AST tree. They're used as
// keys in the spans sidecar (`Spans` in normalize.ts) and threaded through
// the evaluator and compiler so any thrown error can be attributed to the
// offending source position.
//
// Grammar:
//
//   <path>     = '' | <path> '/' <segment>
//   <segment>  = 'params' '/' <index>  |  <kwarg-name>  |  <index>
//
// Examples:
//   ''              — script root
//   '/0'            — first top-level form
//   '/0/params/1'   — second positional arg of the first top-level form
//   '/0/limit'      — kwarg `:limit` of the first top-level form
//
// Centralising the construction here keeps the grammar a single source of
// truth: callers that need to address a sub-node never re-derive the
// separator or the `params/` prefix by hand.

/** Path to a positional parameter of an op call. */
export const paramPath = (base: string, index: number): string => `${base}/params/${index}`;

/** Path to a kwarg of an op call. */
export const kwargPath = (base: string, key: string): string => `${base}/${key}`;
/**
 * Path to a value in a map literal. Uses both the keyword name and the
 * encounter index so duplicate keys keep distinct positions and so the
 * path matches the literal's source order.
 */
export const mapValuePath = (base: string, key: string, index: number): string => `${base}/map/${index}:${key}`;

/**
 * Path to an element of a vector / array node, or to a top-level form when
 * `base` is the empty script-root path.
 */
export const elemPath = (base: string, index: number): string => `${base}/${index}`;

/**
 * Append zero or more segments to a base path, normalising any duplicate
 * separators that result from joining at the script-root boundary. The
 * normalisation collapses runs of `/` so `joinPath('', ['params', 0])`
 * produces `'/params/0'` rather than `'//params/0'`.
 */
export const joinPath = (base: string, segments: readonly (string | number)[]): string =>
  `${base}/${segments.join('/')}`.replace(/\/+/g, '/');
/** Path to the op-name token of an op call. */
export const nameKeyPath = (base: string): string => `${base}/name`;
/**
 * Path to the immediate parent node — drop the last `/segment`. Returns
 * `''` at or above the script root. Used to walk up the spans sidecar to
 * the nearest attributable ancestor when an exact node was not spanned.
 */
export const parentPath = (path: string): string => {
  const i = path.lastIndexOf('/');
  return i <= 0 ? '' : path.slice(0, i);
};
